import { apiErrorMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PdfDocumentState {
  pdf: PDFDocumentProxy | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetch the original PDF and open it with pdf.js.
 *
 * pdf.js and its worker are a large chunk of the bundle and only this one screen needs them, so
 * the module is imported dynamically rather than at the top level. The proxy owns a worker port
 * and a transport; it is destroyed on unmount, and a document that finished loading after the
 * component went away is destroyed immediately instead of leaking.
 */
export const usePdfDocument = (documentId: string | null): PdfDocumentState => {
  const { t } = useTranslation();
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;

    const open = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const blob = await documentsApi.downloadOriginal(documentId);
        const bytes = await blob.arrayBuffer();
        const { loadPdfDocument } = await import('@/utils/pdf');
        loaded = await loadPdfDocument(bytes);
        if (cancelled) {
          loaded.destroy();
          return;
        }
        setPdf(loaded);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, t('review.pdfFailed')));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    open();
    return () => {
      cancelled = true;
      loaded?.destroy();
      setPdf(null);
    };
  }, [documentId, t]);

  return { pdf, isLoading, error };
};
